import { Layer } from 'konva/lib/Layer';
import KonvaService from '.';
import { KonvaUtils } from './konva.utils';
import { KonvaEventObject, Node } from 'konva/lib/Node';
import { Rect } from 'konva/lib/shapes/Rect';
import { Util } from 'konva/lib/Util';
import { Group } from 'konva/lib/Group';
import { Shape, ShapeType } from '../shape';
import { EventType, emitter } from '@/utils/emitter.utils';
import { lockedIdsInCurShapeTree } from '@/hooks';

export class KonvaSelection {
  /**
   * 绘制鼠标选择框的图层，大小范围和konva保存一致
   *
   * @private
   * @type {(Layer | undefined)}
   * @memberof KonvaService
   */
  layerBase: Layer | undefined;
  private drawMode = 'selection';
  private selectRectFill = 'rgba(0, 161, 255, 0.3)';
  // 记录需要选择的名称
  selected: string[] = [];
  private konvaService: KonvaService;
  constructor(konvaService: KonvaService) {
    this.konvaService = konvaService;
    this.bindEvent();
  }

  /**
   * 选择nodes
   * @param nodes Node或name组成的数组
   */
  selectNodes(
    nodeOrNames: (Node | string)[] = [],
    option?: {
      /**
       * 是否触发事件
       */
      fire?: boolean;
      /**
       * 是否判断是否可交互的节点
       */
      interactable?: boolean;
    }
  ) {
    if (!this.konvaService.enable) return;

    const { fire = true, interactable = true } = option ?? {};
    // 获取选择的数据

    // 获取节点，并过滤掉不可用（隐藏或冻结的）
    const nodes: Node[] = [];
    nodeOrNames.map((nodeOrName) => {
      const node =
        typeof nodeOrName == 'string'
          ? this.konvaService
              .getShapeNodes()
              .find((node) => node.name() == nodeOrName)
          : nodeOrName;
      if (node) nodes.push(node);
    });

    // 将选择的节点转为可交互的节点
    let _nodes: Node[] = [...nodes];
    // 是否需要获取可交互的节点,即如果是group的内部元素，将其转为group
    if (interactable) {
      _nodes = [];
      nodes.forEach((node) => {
        node = KonvaUtils.getInteractableNode(node);
        if (!_nodes.includes(node)) _nodes.push(node);
      });
    } else {
      // 收集当前选中节点的所有祖先 group
      const ancestorGroups = new Set<Group>();
      _nodes.forEach((_node) => {
        let parent = _node.parent;

        while (parent && parent?.getType()?.toLowerCase() === 'group') {
          const group = parent as unknown as Group;
          ancestorGroups.add(group);
          KonvaUtils.toggleGroupExplode(group, true);
          parent = parent.parent;
        }
      });

      // 重置不相关 group 的 explode 状态，避免从子级切换到父级时 explode 残留
      const allGroups = this.konvaService.stage.find('#group') as Group[];
      allGroups
        .filter((group) => !ancestorGroups.has(group))
        .forEach((group) => KonvaUtils.toggleGroupExplode(group, false));
    }

    // 去重
    _nodes = [...new Set(_nodes)].filter((node) => !!node.visible());

    // 同名只保留一个节点，避免同一 shape 对应多个 Konva 节点时 Transformer 画出多余边框
    const byName = new Map<string, Node>();
    _nodes.forEach((node) => {
      const name = node.name();
      if (name && !byName.has(name)) byName.set(name, node);
    });
    _nodes = [...byName.values()];

    this.konvaService.tr?.nodes(_nodes);
    const _names = _nodes.map((_node) => _node.name());
    this.selected = _names;

    // 选择时，判断是否可以拖拽修改
    const resizeEnabled = _names.every((_id) => {
      const shape = Shape.getShape(_id);
      return (
        !!shape?.resizeEnabled() &&
        !lockedIdsInCurShapeTree.value.includes(shape.id)
      );
    });
    this.konvaService.tr?.setAttrs({
      resizeEnabled: resizeEnabled
    });

    // 是否可以旋转,只允许单个图片元素可以旋转
    this.konvaService.tr?.setAttrs({
      rotateEnabled: false
    });

    if (fire) {
      emitter.emit(
        EventType['konva:shapes-select'],
        _nodes.map((node) => node.name())
      );
    }
  }

  /**
   * 初始化鼠标选择shape动作，如圈选，或多选
   */
  private bindEvent() {
    const stage = this.konvaService.stage;

    let drawing = false;
    // 鼠标按下时的位置
    let x1: number, y1: number;
    // 鼠标移动时的位置
    let x2: number, y2: number;

    let drawShape: Rect;
    let setDrawShape: (
      x2: number,
      y2: number
    ) => {
      width: number;
      height: number;
      x: number;
      y: number;
    } | void;

    const _selectShapesByMouse = (box: {
      width: number;
      height: number;
      x: number;
      y: number;
    }) => {
      // 选择shape
      const shapes = this.konvaService.getShapeNodes(true);
      const selected =
        !box.height || !box.width
          ? []
          : shapes.filter((shape) =>
              Util.haveIntersection(box, shape.getClientRect())
            );

      const nonPhotoAlbumBg = selected.filter(
        (node) =>
          Shape.getShape(node.getAttr('name'))?.type !== ShapeType.PhotoAlbumBg
      );

      this.selectNodes(nonPhotoAlbumBg.length ? nonPhotoAlbumBg : selected);
    };

    function isContextmenu(e: KonvaEventObject<MouseEvent>) {
      const { ctrlKey, button } = e.evt;
      return button === 2 || (button == 0 && ctrlKey);
    }

    // 开启鼠标滑选
    stage.on('mousedown', (e) => {
      if (this.konvaService.isTransforming || !this.konvaService.enable) return;
      if (isContextmenu(e)) return;
      // do nothing if we mousedown on any shape
      // 当为selection时，鼠标需要落在stage或锁定的元素上
      const target = e.target;
      if (target !== stage && KonvaUtils.isNodeEnabled(target)) return;
      if (drawing) return;
      drawShape?.destroy?.();

      // start draw
      drawing = true;

      e.evt.preventDefault();
      x1 = stage.getPointerPosition()?.x ?? 0;
      y1 = stage.getPointerPosition()?.y ?? 0;
      x2 = x1;
      y2 = y1;

      switch (this.drawMode) {
        case 'selection':
        default:
          drawShape = new Rect({
            fill: this.selectRectFill,
            width: 0,
            height: 0,
            x: x1,
            y: y1,
            listening: false
          });
          setDrawShape = (x2, y2) => {
            const box = {
              x: Math.min(x1, x2),
              y: Math.min(y1, y2),
              width: Math.abs(x2 - x1),
              height: Math.abs(y2 - y1)
            };
            drawShape.setAttrs(box);
            return box;
          };
          break;
      }
      if (!this.layerBase) {
        const layerBase = new Layer();
        this.konvaService.stage.add(layerBase);
        this.layerBase = layerBase;
      }

      this.layerBase?.add(drawShape);
    });

    // 鼠标点击-选择元素
    // 将原来click选择改为mousedown选择
    // 代码必须在其他mousedown之前，这样其他mousedown处理时，元素已选择好
    stage.on('mousedown', (e) => {
      if (!this.konvaService.enable) return;
      const tr = this.konvaService.tr;
      // if we are selecting with rect, do nothing
      if (drawing || !tr) return;
      // windows环境下，用户点击鼠标右键时，不光会触发contextmenu，还会触发click，影响右键菜单功能
      // 所以需要在这里判断一次具体的按键，将非左键的事件屏蔽
      // if (e.evt.button !== 0) {
      //   return;
      // }

      const targetNode = KonvaUtils.getInteractableNode(e.target as Node);

      // if click on empty area - remove all selections
      if (targetNode === stage) {
        this.selectNodes([]);
        return;
      }

      // do nothing if clicked NOT on our rectangles
      if (!this.konvaService.getShapeNodes().includes(targetNode)) {
        return;
      }

      if (!KonvaUtils.isNodeEnabled(targetNode)) return;

      // do we pressed shift or ctrl?
      const metaPressed = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
      const isSelected = tr.nodes().indexOf(targetNode) >= 0;
      if (isSelected) return;

      let selectedNodes: Node[] = [];
      if (!metaPressed && !isSelected) {
        // if no key pressed and the node is not selected
        // select just one
        selectedNodes = [targetNode];
      } else if (metaPressed && isSelected) {
        // if we pressed keys and node was selected
        // we need to remove it from selection:
        const nodes = tr.nodes().slice(); // use slice to have new copy of array
        // remove node from array
        nodes.splice(nodes.indexOf(targetNode), 1);
        selectedNodes = nodes;
      } else if (metaPressed && !isSelected) {
        // add the node into selection
        const selects = tr.nodes();
        const isPhotoAlbumBg =
          Shape.getShape(targetNode.getAttr('name'))?.type ===
          ShapeType.PhotoAlbumBg;

        if (
          (selects.length && isPhotoAlbumBg) ||
          (!isPhotoAlbumBg &&
            selects.some(
              (node) =>
                Shape.getShape(node.getAttr('name'))?.type ===
                ShapeType.PhotoAlbumBg
            ))
        ) {
          selectedNodes = selects;
        } else {
          selectedNodes = selects.concat([targetNode]);
        }
      }
      this.selectNodes(selectedNodes);
      // 为了防止dblclick不触发，先注释
      // const data = e.target?.getAttr('data');
      // if (data.shapeTag === ShapeTag.Text) return;
      //
    });

    stage.on('mousemove', (e) => {
      // do nothing if we didn't start selection
      if (!drawing || !this.konvaService.enable) return;

      e.evt.preventDefault();
      x2 = stage.getPointerPosition()?.x ?? 0;
      y2 = stage.getPointerPosition()?.y ?? 0;

      const box = setDrawShape?.(x2, y2);
      if (this.drawMode == 'selection' && box) {
        _selectShapesByMouse(box);
      }
    });

    stage.on('mouseup', (e) => {
      if (!this.konvaService.enable) return;
      if (!drawing) return;
      if (isContextmenu(e)) return;

      e.evt.preventDefault();
      if (!drawShape) return;
      // update visibility in timeout, so we can check it in click event
      setTimeout(() => {
        drawing = false;
        drawShape.destroy();
        this.layerBase?.destroy();
        this.layerBase = undefined;
      });

      const box = drawShape.getClientRect();
      // console.log('绘制的选择框为', box);
      _selectShapesByMouse(box);
    });

    /*** 刚刚发生的鼠标事件，是否发生在画布已有选区中（一个或多个图层组成的最大矩形范围）*/
    // const isHitOnSelectedRange = () => {
    //   const box = {
    //     x: stage.getPointerPosition()?.x ?? 0,
    //     y: stage.getPointerPosition()?.y ?? 0,
    //     width: 1,
    //     height: 1
    //   };
    //   const box2 = this.konvaService.tr?.getClientRect();
    //   return box2 && Util.haveIntersection(box, box2);
    // };

    // stage.on('contextmenu', (e) => {
    //   // 如果右键点击的不是已有的选区，才需要改变画布的selection
    //   if (!isHitOnSelectedRange()) {
    //     if (e.target === stage) {
    //       this.selectNodes([]);
    //     } else {
    //       this.selectNodes([e.target]);
    //     }
    //   }
    // });

    stage.on('mouseleave', () => {
      // console.log('mouseleave');
      setTimeout(() => {
        drawing = false;
        drawShape?.destroy();
        this.layerBase?.destroy();
        this.layerBase = undefined;
      });
    });
  }
}
